import { create } from 'zustand'
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '../config/firebase'
import {
  Report,
  ReportReason,
  ReportStatus,
  ReportType,
} from '../models/Report'
import { useAuthStore } from './authStore'

interface ReportState {
  reports: Report[]
  loading: boolean
  error: string | null
  fetchReports: () => Promise<void>
  updateStatus: (id: string, status: ReportStatus) => Promise<void>
  pendingCount: () => number
}

function toDate(value: unknown): Date {
  if (value instanceof Timestamp) return value.toDate()
  if (value instanceof Date) return value
  return new Date()
}

export const useReportStore = create<ReportState>((set, get) => ({
  reports: [],
  loading: false,
  error: null,

  fetchReports: async () => {
    set({ loading: true, error: null })
    try {
      const q = query(collection(db, 'reports'), orderBy('createdAt', 'desc'))
      const snapshot = await getDocs(q)

      const reports: Report[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data()
        return {
          id: docSnap.id,
          type: (data.type ?? 'discussion') as ReportType,
          discussionId: data.discussionId ?? '',
          replyId: data.replyId ?? null,
          targetUserId: data.targetUserId ?? '',
          reporterId: data.reporterId ?? '',
          reason: (data.reason ?? 'other') as ReportReason,
          detail: data.detail ?? '',
          contentSnapshot: data.contentSnapshot ?? '',
          createdAt: toDate(data.createdAt),
          status: (data.status ?? 'pending') as ReportStatus,
          handledAt: data.handledAt ? toDate(data.handledAt) : undefined,
          handledBy: data.handledBy ?? undefined,
        }
      })

      set({ reports, loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '載入檢舉失敗',
      })
    }
  },

  updateStatus: async (id: string, status: ReportStatus) => {
    set({ loading: true, error: null })
    try {
      const admin = useAuthStore.getState().admin
      await updateDoc(doc(db, 'reports', id), {
        status,
        handledAt: serverTimestamp(),
        handledBy: admin?.id ?? '',
      })
      await get().fetchReports()
      set({ loading: false })
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : '更新檢舉狀態失敗',
      })
      throw error
    }
  },

  pendingCount: () => get().reports.filter((r) => r.status === 'pending').length,
}))
